import { APP_INITIALIZER, type EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';

import { environment } from 'environments/environment';

import { HmacService } from '../services/hmac.service';
import { BatchedIngestClient } from './batched-ingest.client';
import { MetricsSpanProcessor } from './metrics-span.processor';
import { MetricsService } from './metrics.service';
import { initializeOtelWeb } from './otel-web.bootstrap';

/**
 * Provides the SPA metrics pipeline:
 *
 *   1. Wires HmacService → BatchedIngestClient
 *   2. Builds MetricsSpanProcessor with the client + tenant resolver
 *   3. Initializes a WebTracerProvider that registers the processor
 *   4. Starts MetricsService (router + bootstrap trackers)
 *   5. Hooks `visibilitychange` and `pagehide` to drain the buffer on unload
 *      (uses fetch keepalive so the request survives tab close)
 *
 * When `environment.metrics?.enabled !== true`, this provider is a no-op:
 * APP_INITIALIZER resolves immediately and no spans are produced.
 */
export function provideMetrics(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const hmacService = inject(HmacService);
        const metrics = inject(MetricsService);

        return async (): Promise<void> => {
          const cfg = environment.metrics;
          if (!cfg || !cfg.enabled) {
            return;
          }

          const client = new BatchedIngestClient({
            ingestUrl: cfg.ingestUrl,
            hmacPath: cfg.hmacPath,
            hmacSecret: environment.hmacSecret,
            hmacService,
          });

          const processor = new MetricsSpanProcessor({
            client,
            emitter: {
              service: cfg.serviceName ?? 'spri-client',
              environment: cfg.environmentName,
            },
            flushSize: cfg.flushSize,
            flushIntervalMs: cfg.flushIntervalMs,
            tenantIdProvider: () => sessionStorage.getItem('tenantId'),
            onWarn: (msg, ctx) => {
              console.warn(`[metrics] ${msg}`, ctx ?? {});
            },
          });

          await initializeOtelWeb({
            serviceName: cfg.serviceName ?? 'spri-client',
            environmentName: cfg.environmentName,
            processor,
          });

          metrics.init();

          // Drain on tab hide / unload. Using fetch keepalive (set by the
          // ingest client) means these final POSTs survive the tab close.
          const flushOnExit = () => {
            void processor.forceFlush();
          };
          if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'hidden') {
                flushOnExit();
              }
            });
          }
          if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', flushOnExit);
          }
        };
      },
    },
  ]);
}
